import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  AreaChart, Area,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  CpuChipIcon,
  CurrencyDollarIcon,
  CalculatorIcon,
  ClockIcon,
  CodeBracketIcon,
  UserGroupIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { useUser } from '../contexts/UserContext';

const COLORS = {
  blue: '#3B82F6',
  green: '#10B981',
  amber: '#F59E0B',
  purple: '#8B5CF6',
  red: '#EF4444',
  indigo: '#6366F1',
};

const shortEmail = (email) => email ? email.split('@')[0] : email;

const fmt = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};

const KpiCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white shadow rounded-lg p-5 flex items-center gap-4">
    <div className={`p-3 rounded-full ${color}`}>
      <Icon className="h-6 w-6 text-white" />
    </div>
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-lg font-semibold text-gray-800 mt-8 mb-3">{children}</h2>
);

function exportToExcel({ days, summary, dailyCost, dailyTokens, dailyCommits, userUsage, userEditDecisions, userSourceBreakdown, sourceBreakdown, languages, editDecisions, activeTime }) {
  const wb = XLSX.utils.book_new();
  const s = (rows, name) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length ? rows : [{}]), name);

  s([{
    'Period (days)': days,
    'Total Cost (USD)': summary?.totalCostUsd?.toFixed(4) ?? 0,
    'Total Tokens': summary?.totalTokens ?? 0,
    'Total Sessions': summary?.totalSessions ?? 0,
    'Total Active Hours': summary?.totalActiveTimeHours ?? 0,
    'Total Commits': summary?.totalCommits ?? 0,
    'Total PRs': summary?.totalPullRequests ?? 0,
    'Active Users': summary?.activeUserCount ?? 0,
    'User Active Hours': activeTime.find(a => a.type === 'user')?.totalHours?.toFixed(2) ?? 0,
    'CLI Processing Hours': activeTime.find(a => a.type === 'cli')?.totalHours?.toFixed(2) ?? 0,
  }], 'Summary');

  s(userUsage.map(u => ({
    'User': u.userEmail,
    'Cost (USD)': u.costUsd?.toFixed(4),
    'Tokens': u.totalTokens,
    'Sessions': u.sessionCount,
    'Active Hours': u.activeTimeHours?.toFixed(2),
    'Commits': u.commits,
    'PRs': u.pullRequests,
  })), 'Per User');

  s(userEditDecisions.map(u => ({
    'User': u.userEmail,
    'Accepted': u.acceptCount,
    'Rejected': u.rejectCount,
    'Accept Rate (%)': u.acceptRate,
  })), 'Accept Rate per User');

  s(userSourceBreakdown.map(r => ({
    'User': r.userEmail,
    'Source': r.source,
    'Sessions': r.sessionCount,
    'Cost (USD)': r.costUsd?.toFixed(4),
  })), 'Source per User');

  s(dailyCost.map(r => ({ 'Date': r.date, 'Cost (USD)': r.costUsd })), 'Daily Cost');

  s(dailyTokens.map(r => ({
    'Date': r.date,
    'Input': r.input,
    'Output': r.output,
    'Cache Read': r.cacheRead,
    'Cache Write': r.cacheWrite,
  })), 'Daily Tokens');

  s(dailyCommits.map(r => ({ 'Date': r.date, 'Commits': r.commits, 'PRs': r.pullRequests })), 'Daily Commits');

  s(sourceBreakdown.map(r => ({ 'Source': r.source, 'Sessions': r.sessions, 'Cost (USD)': r.costUsd })), 'Source Breakdown');

  s(languages.map(r => ({ 'Language': r.language, 'Accepted': r.accepted, 'Rejected': r.rejected })), 'Top Languages');

  s(editDecisions.map(r => ({ 'Decision': r.decision, 'Count': r.count })), 'Edit Decisions');

  const date = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `ClaudeUsage_${days}d_${date}.xlsx`);
}

export default function ClaudeUsageDashboard() {
  const { isAdmin } = useUser();
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [dailyCost, setDailyCost] = useState([]);
  const [dailyTokens, setDailyTokens] = useState([]);
  const [userUsage, setUserUsage] = useState([]);
  const [editDecisions, setEditDecisions] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [activeTime, setActiveTime] = useState([]);
  const [sourceBreakdown, setSourceBreakdown] = useState([]);
  const [dailyCommits, setDailyCommits] = useState([]);
  const [userEditDecisions, setUserEditDecisions] = useState([]);
  const [userSourceBreakdown, setUserSourceBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAll(days);
  }, [days]);

  const loadAll = async (d) => {
    setLoading(true);
    setError(null);
    try {
      const [s, dc, dt, uu, ed, lg, at, sb, dco, ued, usb] = await Promise.all([
        apiService.getClaudeUsageSummary(d),
        apiService.getClaudeDailyCost(d),
        apiService.getClaudeDailyTokens(d),
        apiService.getClaudeUserUsage(d),
        apiService.getClaudeEditDecisions(d),
        apiService.getClaudeLanguages(d),
        apiService.getClaudeActiveTime(d),
        apiService.getClaudeSourceBreakdown(d),
        apiService.getClaudeDailyCommits(d),
        apiService.getClaudeUserEditDecisions(d),
        apiService.getClaudeUserSourceBreakdown(d),
      ]);
      setSummary(s);
      setDailyCost(dc);
      setDailyTokens(dt);
      setUserUsage(uu);
      setEditDecisions(ed);
      setLanguages(lg);
      setActiveTime(at);
      setSourceBreakdown(sb);
      setDailyCommits(dco);
      setUserEditDecisions(ued);
      setUserSourceBreakdown(usb);
    } catch (err) {
      setError('Failed to load Claude usage data. Check that the App Insights resource is accessible.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin()) return null;

  const dayOptions = [7, 30, 90];

  const userTime = activeTime.find(a => a.type === 'user')?.totalHours ?? 0;
  const cliTime = activeTime.find(a => a.type === 'cli')?.totalHours ?? 0;

  const acceptCount = editDecisions.find(d => d.decision === 'accept')?.count ?? 0;
  const rejectCount = editDecisions.find(d => d.decision === 'reject')?.count ?? 0;
  const acceptRate = acceptCount + rejectCount > 0
    ? Math.round((acceptCount / (acceptCount + rejectCount)) * 100)
    : null;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <CpuChipIcon className="h-7 w-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Claude Code Usage</h1>
            <p className="text-sm text-gray-500">Team AI usage via Azure Application Insights</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Export button */}
          {!loading && !error && (
            <button
              onClick={() => exportToExcel({ days, summary, dailyCost, dailyTokens, dailyCommits, userUsage, userEditDecisions, userSourceBreakdown, sourceBreakdown, languages, editDecisions, activeTime })}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              title="Export to Excel"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Export
            </button>
          )}

          {/* Time range picker */}
          <div className="flex rounded-md shadow-sm border border-gray-300 overflow-hidden">
            {dayOptions.map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  days === d
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4 mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <KpiCard label="Total Cost" value={`$${summary?.totalCostUsd?.toFixed(2) ?? '0.00'}`} icon={CurrencyDollarIcon} color="bg-blue-500" />
            <KpiCard label="Tokens" value={fmt(summary?.totalTokens)} icon={CalculatorIcon} color="bg-purple-500" />
            <KpiCard label="Sessions" value={fmt(summary?.totalSessions)} icon={CpuChipIcon} color="bg-indigo-500" />
            <KpiCard label="Active Hours" value={`${summary?.totalActiveTimeHours ?? 0}h`} icon={ClockIcon} color="bg-amber-500" />
            <KpiCard label="Commits" value={fmt(summary?.totalCommits)} icon={CodeBracketIcon} color="bg-green-500" />
            <KpiCard label="Active Users" value={summary?.activeUserCount ?? 0} icon={UserGroupIcon} color="bg-rose-500" />
          </div>

          {/* Daily Spend */}
          <SectionTitle>Daily Spend</SectionTitle>
          <div className="bg-white shadow rounded-lg p-5">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={dailyCost}>
                <defs>
                  <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
                <Tooltip formatter={(v) => [`$${v.toFixed(4)}`, 'Cost']} labelFormatter={l => `Date: ${l}`} />
                <Area type="monotone" dataKey="costUsd" stroke={COLORS.blue} fill="url(#costGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Token Consumption */}
          <SectionTitle>Token Consumption by Type</SectionTitle>
          <div className="bg-white shadow rounded-lg p-5">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={dailyTokens}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmt} />
                <Tooltip formatter={(v, n) => [fmt(v), n]} labelFormatter={l => `Date: ${l}`} />
                <Legend />
                <Bar dataKey="input" stackId="a" fill={COLORS.blue} name="Input" />
                <Bar dataKey="output" stackId="a" fill={COLORS.green} name="Output" />
                <Bar dataKey="cacheRead" stackId="a" fill={COLORS.amber} name="Cache Read" />
                <Bar dataKey="cacheWrite" stackId="a" fill={COLORS.purple} name="Cache Write" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Source + Edit Decisions side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {/* VS Code vs Cowork */}
            <div className="bg-white shadow rounded-lg p-5">
              <h2 className="text-base font-semibold text-gray-800 mb-3">VS Code vs Cowork</h2>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={sourceBreakdown} dataKey="costUsd" nameKey="source" cx="50%" cy="50%" outerRadius={75} label={({ source, percent }) => `${source} ${(percent * 100).toFixed(0)}%`}>
                    {sourceBreakdown.map((_, i) => (
                      <Cell key={i} fill={i === 0 ? COLORS.blue : COLORS.green} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => [`$${v.toFixed(3)}`, 'Cost']} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Edit Decisions */}
            <div className="bg-white shadow rounded-lg p-5">
              <h2 className="text-base font-semibold text-gray-800 mb-1">Edit Decisions</h2>
              {acceptRate != null && (
                <p className="text-sm text-gray-500 mb-2">Accept rate: <span className="font-semibold text-green-600">{acceptRate}%</span></p>
              )}
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={editDecisions} dataKey="count" nameKey="decision" cx="50%" cy="50%" outerRadius={75} label={({ decision, percent }) => `${decision} ${(percent * 100).toFixed(0)}%`}>
                    {editDecisions.map((entry, i) => (
                      <Cell key={i} fill={entry.decision === 'accept' ? COLORS.green : COLORS.red} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Languages */}
          {languages.length > 0 && (
            <>
              <SectionTitle>Top Languages (Edit Decisions)</SectionTitle>
              <div className="bg-white shadow rounded-lg p-5">
                <ResponsiveContainer width="100%" height={Math.max(200, languages.length * 32)}>
                  <BarChart data={languages} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="language" type="category" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="accepted" fill={COLORS.green} name="Accepted" />
                    <Bar dataKey="rejected" fill={COLORS.red} name="Rejected" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {/* Active Time */}
          <SectionTitle>Active Time Split</SectionTitle>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white shadow rounded-lg p-5 flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-500">
                <ClockIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500">User Active Time</p>
                <p className="text-2xl font-bold text-gray-900">{userTime.toFixed(1)}h</p>
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-5 flex items-center gap-4">
              <div className="p-3 rounded-full bg-purple-500">
                <CpuChipIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500">CLI Processing Time</p>
                <p className="text-2xl font-bold text-gray-900">{cliTime.toFixed(1)}h</p>
              </div>
            </div>
          </div>

          {/* Commits Over Time */}
          <SectionTitle>Commits &amp; PRs Over Time</SectionTitle>
          <div className="bg-white shadow rounded-lg p-5">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={dailyCommits}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip labelFormatter={l => `Date: ${l}`} />
                <Legend />
                <Bar dataKey="commits" fill={COLORS.green} name="Commits" />
                <Bar dataKey="pullRequests" fill={COLORS.indigo} name="PRs" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Commits & PRs per User */}
          {userUsage.length > 0 && (
            <>
              <SectionTitle>Commits &amp; PRs per User</SectionTitle>
              <div className="bg-white shadow rounded-lg p-5">
                <ResponsiveContainer width="100%" height={Math.max(200, userUsage.length * 36)}>
                  <BarChart data={userUsage} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis dataKey="userEmail" type="category" tick={{ fontSize: 11 }} width={120} tickFormatter={shortEmail} />
                    <Tooltip labelFormatter={shortEmail} />
                    <Legend />
                    <Bar dataKey="commits" fill={COLORS.green} name="Commits" />
                    <Bar dataKey="pullRequests" fill={COLORS.indigo} name="PRs" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {/* Accept Rate per User */}
          {userEditDecisions.length > 0 && (
            <>
              <SectionTitle>Accept Rate per User</SectionTitle>
              <div className="bg-white shadow rounded-lg p-5">
                <ResponsiveContainer width="100%" height={Math.max(200, userEditDecisions.length * 36)}>
                  <BarChart data={userEditDecisions} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
                    <YAxis dataKey="userEmail" type="category" tick={{ fontSize: 11 }} width={120} tickFormatter={shortEmail} />
                    <Tooltip labelFormatter={shortEmail} formatter={(v, n) => [n === 'acceptRate' ? `${v}%` : fmt(v), n === 'acceptRate' ? 'Accept Rate' : n === 'acceptCount' ? 'Accepted' : 'Rejected']} />
                    <Legend formatter={v => v === 'acceptRate' ? 'Accept Rate %' : v === 'acceptCount' ? 'Accepted' : 'Rejected'} />
                    <Bar dataKey="acceptCount" stackId="a" fill={COLORS.green} name="acceptCount" />
                    <Bar dataKey="rejectCount" stackId="a" fill={COLORS.red} name="rejectCount" />
                  </BarChart>
                </ResponsiveContainer>
                {/* Accept rate as a simple table beneath the chart */}
                <table className="min-w-full text-sm mt-4">
                  <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                    <tr>
                      <th className="px-4 py-2 text-left">User</th>
                      <th className="px-4 py-2 text-right">Accepted</th>
                      <th className="px-4 py-2 text-right">Rejected</th>
                      <th className="px-4 py-2 text-right">Accept Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {userEditDecisions.map((u, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-900">{shortEmail(u.userEmail)}</td>
                        <td className="px-4 py-2 text-right font-mono">{fmt(u.acceptCount)}</td>
                        <td className="px-4 py-2 text-right font-mono">{fmt(u.rejectCount)}</td>
                        <td className="px-4 py-2 text-right font-semibold text-green-600">{u.acceptRate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Per-User Source Breakdown */}
          {userSourceBreakdown.length > 0 && (
            <>
              <SectionTitle>Per-User Source Breakdown (VS Code vs Cowork)</SectionTitle>
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3 text-left">User</th>
                      <th className="px-4 py-3 text-left">Source</th>
                      <th className="px-4 py-3 text-right">Sessions</th>
                      <th className="px-4 py-3 text-right">Cost (USD)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {userSourceBreakdown.map((r, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-900">{shortEmail(r.userEmail)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${r.source === 'cowork' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                            {r.source}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">{r.sessionCount}</td>
                        <td className="px-4 py-3 text-right font-mono">${r.costUsd.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Per-User Table */}
          <SectionTitle>Per-User Breakdown</SectionTitle>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left">User</th>
                  <th className="px-4 py-3 text-right">Cost (USD)</th>
                  <th className="px-4 py-3 text-right">Tokens</th>
                  <th className="px-4 py-3 text-right">Sessions</th>
                  <th className="px-4 py-3 text-right">Active Hrs</th>
                  <th className="px-4 py-3 text-right">Commits</th>
                  <th className="px-4 py-3 text-right">PRs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {userUsage.map((u, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">{shortEmail(u.userEmail)}</td>
                    <td className="px-4 py-3 text-right font-mono">${u.costUsd.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmt(u.totalTokens)}</td>
                    <td className="px-4 py-3 text-right">{u.sessionCount}</td>
                    <td className="px-4 py-3 text-right">{u.activeTimeHours.toFixed(1)}</td>
                    <td className="px-4 py-3 text-right">{u.commits}</td>
                    <td className="px-4 py-3 text-right">{u.pullRequests}</td>
                  </tr>
                ))}
                {userUsage.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-400">No data for this period</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
